
import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { UploadIcon, DocumentIcon } from '../BorrowingsIcons';

interface DocumentUploadFormProps {
  isProcessing: boolean;
  onUpload: (files: FileList | null) => void;
}

export const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({ isProcessing, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles(e.dataTransfer.files);
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(e.target.files);
    }
  };
  
  const handleSubmit = () => {
    onUpload(selectedFiles);
  };

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center ${
          dragActive ? "border-primary bg-primary/5" : "border-muted-foreground/20"
        } transition-colors`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center space-y-4">
          {isProcessing ? (
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              <p className="text-sm">Processing document(s)...</p>
            </div>
          ) : (
            <>
              <div className="rounded-full bg-primary/10 p-3">
                <UploadIcon className="h-8 w-8 text-primary" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">
                  Drag and drop your loan documents here
                </p>
                <p className="text-xs text-muted-foreground">
                  PDF, DOC, DOCX, XLS, XLSX files up to 10MB
                </p>
                
                <label htmlFor="file-upload" className="cursor-pointer">
                  <div className="mt-2 inline-flex text-sm text-primary hover:underline">
                    or browse files
                  </div>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    multiple
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                </label>
              </div>
            </>
          )}
        </div>
      </div>
      
      {selectedFiles && selectedFiles.length > 0 && !isProcessing && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Selected Files:</p>
          <div className="space-y-2">
            {Array.from(selectedFiles).map((file, index) => (
              <Card key={index} className="bg-muted/50">
                <CardContent className="p-3 flex items-center gap-2">
                  <DocumentIcon className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 truncate">
                    <p className="text-sm truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)}KB
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <Button className="w-full" onClick={handleSubmit}>
            Parse Documents
          </Button>
        </div>
      )}
    </div>
  );
};
